import { useState } from 'react';

const parts = [
  {
    num: 1,
    title: 'Les fondations',
    subtitle: 'Comprendre la monnaie et les dividendes',
    chapters: [
      'Pourquoi faut-il investir ?',
      'Qu\'est-ce qu\'une action ?',
      'Comment fonctionne la bourse',
      'Qu\'est-ce qu\'un dividende',
      'Pourquoi les dividendes sont puissants',
      'Les fausses promesses du rendement élevé',
    ],
  },
  {
    num: 2,
    title: 'L\'analyse financière simplifiée',
    subtitle: 'Apprendre à lire les chiffres',
    chapters: [
      'Lire un bilan et un compte de résultat',
      'Les ratios essentiels : ROIC, ROE, FCF, dette, payout, valorisation',
      'Le cash flow libre (FCF) : la vérité des chiffres',
      'Évaluer le management et l\'allocation du capital',
      'Le "moat" : comprendre l\'avantage compétitif durable',
    ],
  },
  {
    num: 3,
    title: 'La croissance',
    subtitle: 'Le vrai moteur de la richesse',
    chapters: [
      'Les trois moteurs de la performance boursière',
      'Comprendre la croissance rentable (ROIC et croissance interne/externe)',
      'Le dividende croissant : la signature d\'un business sain',
      'La magie du temps et de la capitalisation',
    ],
  },
  {
    num: 4,
    title: 'Construire un portefeuille de qualité',
    subtitle: 'Stratégie & diversification',
    chapters: [
      'Les enveloppes d\'investissement : PEA, CTO, assurance-vie, société',
      'Stratégie hybride : ETF socle + 5 à 10 actions de qualité',
      'Diversification et pondération : trouver l\'équilibre parfait',
      'Renforcements et rééquilibrages : la méthode DCA qualitative',
      'Suivi & audit : les indicateurs clés et les signaux de vente',
      'Portefeuilles types : débutant / intermédiaire / confirmé',
    ],
  },
  {
    num: 5,
    title: 'Psychologie & discipline',
    subtitle: 'L\'investisseur qui dure',
    chapters: [
      'Les biais cognitifs majeurs : peur, avidité, FOMO, ancrage',
      'L\'art de la patience : comment durer 20 ans sur les marchés',
      'Agir avec lucidité en période de crise',
      'Construire sa routine mentale et son suivi émotionnel',
    ],
  },
  {
    num: 6,
    title: 'Fiscalité & structuration patrimoniale',
    subtitle: 'Optimiser & transmettre',
    chapters: [
      'Fiscalité : PEA, CTO, société, PFU, IS',
      'Holding patrimoniale : régime mère-fille, CCA, optimisations',
      'Fiscalité internationale : retenues à la source & conventions fiscales',
      'Transmission & démembrement : préparer l\'héritage intelligent',
    ],
  },
  {
    num: 7,
    title: 'Cas pratiques & plans d\'action',
    subtitle: 'Passer à l\'action concrètement',
    chapters: [
      'Construire SON premier portefeuille (10k / 50k / 100k)',
      'Études complètes : Visa, L\'Oréal, Air Liquide, Microsoft',
      'Plan d\'investissement automatique (mensuel / trimestriel)',
      'Les 10 erreurs les plus coûteuses des investisseurs particuliers',
    ],
  },
  {
    num: 8,
    title: 'Annexes & outils pratiques',
    subtitle: 'Ressources pour aller plus loin',
    chapters: [
      'Fiches d\'analyse prêtes à remplir',
      'Tableurs de suivi des dividendes',
      'Glossaire complet (150+ notions clés)',
      'Modèles & checklists pratiques',
      'Roadmap 90 jours vers l\'autonomie financière',
      'Lectures recommandées & ressources avancées',
    ],
  },
];

const ProgramSection = () => {
  const [openPart, setOpenPart] = useState<number | null>(null);

  return (
    <section className="bg-[#1E0750] py-16 px-8 lg:px-16">
      <div className="max-w-[1272px] mx-auto">
        <div className="text-center mb-12">
          <div className="inline-block border border-[#A3E635]/40 text-[#A3E635] text-xs font-bold px-4 py-2 rounded-full mb-4 tracking-widest uppercase">
            Programme complet
          </div>
          <h2 className="text-white font-black text-3xl lg:text-4xl">
            Dans cette Méthode,{' '}
            <span className="text-[#A3E635]">chaque étape a un but précis</span>
          </h2>
          <p className="text-white/60 text-sm mt-3 max-w-[540px] mx-auto">
            8 parties · 34+ chapitres vidéo · Du débutant complet à l'investisseur autonome et performant.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {parts.map((part) => {
            const isOpen = openPart === part.num;
            return (
              <div
                key={part.num}
                className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                  isOpen
                    ? 'border-[#A3E635]/50 bg-[#2D1280]'
                    : 'border-white/10 bg-[#2D1280]/60 hover:border-white/25'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenPart(isOpen ? null : part.num)}
                  className="w-full flex items-center gap-4 px-6 py-5 cursor-pointer text-left"
                >
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-[#A3E635]/15 border border-[#A3E635]/30">
                    <span className="text-[#A3E635] font-black text-sm">{part.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm md:text-base leading-snug">
                      Partie {part.num} — {part.title}
                    </p>
                    <p className="text-white/45 text-xs mt-0.5">{part.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="hidden sm:inline-block text-[#A3E635]/70 text-xs font-medium bg-[#A3E635]/10 px-3 py-1 rounded-full whitespace-nowrap">
                      {part.chapters.length} chapitres
                    </span>
                    <div className={`w-5 h-5 flex items-center justify-center text-white/50 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-lg" />
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-5 border-t border-white/10 pt-4">
                    <ul className="flex flex-col gap-2">
                      {part.chapters.map((chapter, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                            <i className="ri-play-circle-fill text-[#A3E635] text-sm" />
                          </div>
                          <span className="text-white/80 text-sm leading-snug">{chapter}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-white/30 text-xs mt-6">
          + Fiches, tableurs, glossaire 150+ notions, checklists et roadmap 90 jours inclus
        </p>
      </div>
    </section>
  );
};

export default ProgramSection;
